import { FaTimes, FaLightbulb, FaInfoCircle } from "react-icons/fa";

interface HowToGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

// HowToGuide modal component
const HowToGuide: React.FC<HowToGuideProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            How to Create Custom Tools
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={24} />
          </button>
        </div>

        <div className="px-6 py-4">
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <FaLightbulb className="text-yellow-500 mr-2" /> Understanding
              Custom Tools
            </h3>
            <p className="text-gray-600 mb-4">
              A custom tool is an instruction set that tells AI what to do with
              your information. Think of it like a recipe:
            </p>
            <ul className="list-disc pl-8 space-y-2 text-gray-600 mb-4">
              <li>
                <strong>Tool Name</strong> - What you call your tool (e.g.,
                "Content Summarizer")
              </li>
              <li>
                <strong>Instructions</strong> - Step-by-step directions for the
                AI to follow
              </li>
              <li>
                <strong>Variables</strong> - What you'll provide when using the
                tool
              </li>
              <li>
                <strong>Results</strong> - What the tool will give back to you
              </li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <span className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-2">
                1
              </span>{" "}
              Tool Name
            </h3>
            <p className="text-gray-600 mb-4">
              Choose a clear, descriptive name that explains what your tool
              does. Good examples:
            </p>
            <ul className="list-disc pl-8 space-y-1 text-gray-600">
              <li>"Email Summarizer"</li>
              <li>"Customer Service Response Generator"</li>
              <li>"Research Paper Question Creator"</li>
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <span className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-2">
                2
              </span>{" "}
              Instructions
            </h3>
            <p className="text-gray-600 mb-4">
              Instructions tell the AI exactly what you want it to do. Use
              clear, step-by-step directions.
            </p>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Example:</strong>
              </p>
              <p className="text-gray-600">
                Create a summary of the {"{{text}}"} that is {"{{length}}"} in
                length. Focus on {"{{focus}}"} aspects and include {"{{count}}"}{" "}
                key points.
              </p>
            </div>
            <p className="text-gray-600 mb-2">
              Notice the use of{" "}
              <code className="bg-gray-100 px-1">{"{{variables}}"}</code> in the
              instructions - these are placeholders for information you'll
              provide each time you use the tool.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <span className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-2">
                3
              </span>{" "}
              Variables
            </h3>
            <p className="text-gray-600 mb-4">
              These are the variables - the information your tool needs to work.
              Each variable needs:
            </p>
            <ul className="list-disc pl-8 space-y-2 text-gray-600 mb-4">
              <li>
                <strong>Name</strong> - Must match the variable used in
                instructions (e.g., "text")
              </li>
              <li>
                <strong>Description</strong> - Explains what information is
                needed
              </li>
              <li>
                <strong>Type</strong> - How the information should be entered
                (text, dropdown menu, etc.)
              </li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <p className="text-blue-800 font-medium">
                Each variable in your instructions (like {"{{text}}"}) needs a
                corresponding variable definition.
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <span className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-2">
                4
              </span>{" "}
              Results
            </h3>
            <p className="text-gray-600 mb-4">
              Define what outputs your tool will produce. Most tools need at
              least one result.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                <p className="font-medium text-gray-700 mb-1">
                  Simple Example:
                </p>
                <p className="text-gray-600">
                  Output: "summary" (string)
                  <br />
                  Description: "The generated summary"
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                <p className="font-medium text-gray-700 mb-1">
                  Multiple Results Example:
                </p>
                <p className="text-gray-600">
                  Output 1: "summary" (string)
                  <br />
                  Output 2: "key_points" (list)
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <FaInfoCircle className="text-blue-500 mr-2" /> Tips for Success
            </h3>
            <ul className="list-disc pl-8 space-y-2 text-gray-600">
              <li>Use the templates to get started quickly</li>
              <li>
                Make sure every variable in your instructions has a matching
                variable definition
              </li>
              <li>Be specific and clear with your instructions</li>
              <li>
                Test your tool with different inputs to ensure it works as
                expected
              </li>
              <li>
                Start simple, then add more complexity as you become familiar
                with custom tools
              </li>
            </ul>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-yellow-400 text-gray-800 rounded-md hover:bg-yellow-500 transition-colors flex items-center justify-center"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowToGuide;
